import {createContext, useContext, useMemo, type PropsWithChildren} from 'react'
import {noop} from '@github-ui/noop'
import {announce} from '@github-ui/aria-live'
import {useListItemTitle} from './TitleContext'

export type SelectionContextProps = {
  isSelected: boolean
  onSelect: (isSelected: boolean) => void
}

const SelectionContext = createContext<SelectionContextProps>({isSelected: false, onSelect: noop})

type SelectionProviderProps = PropsWithChildren & {value: SelectionContextProps}

export const SelectionProvider = ({children, value: {isSelected, onSelect}}: SelectionProviderProps) => {
  const {title} = useListItemTitle()

  const contextProps = useMemo(() => {
    const onSelectAndAnnounce = (selected: boolean) => {
      announce(selected ? `Selected. ${title}.` : `Unselected. ${title}.`)
      onSelect(selected)
    }
    return {isSelected, onSelect: onSelectAndAnnounce}
  }, [isSelected, onSelect, title])
  return <SelectionContext.Provider value={contextProps}>{children}</SelectionContext.Provider>
}

export const useListItemSelection = () => {
  return useContext(SelectionContext)
}

try{ SelectionContext.displayName ||= 'SelectionContext' } catch {}
try{ SelectionProvider.displayName ||= 'SelectionProvider' } catch {}